"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
type ProjectGroup = "Roofs" | "Flat Roofs" | "Fascias & Guttering";
interface ProjectItem {
  id: string;
  file: string;
  caption: string;
  alt: string;
  group: ProjectGroup;
  to: string;
}
const BASE_URL = "https://qualityroofsandlandscapes.co.uk/wp-content/uploads/2026/10/";
const PROJECT_PHOTOS: ProjectItem[] = [{
  id: "photo-1",
  file: "roof-soffit-fascia-southampton.jpg",
  caption: "New roof edge, fascia and soffit",
  alt: "New roof edge with fascia and soffit in Southampton",
  group: "Roofs",
  to: "/RoofingServicesPage"
}, {
  id: "photo-2",
  file: "grp-flat-roof-southampton.jpg",
  caption: "GRP flat roof",
  alt: "GRP fibreglass flat roof installed in Southampton",
  group: "Flat Roofs",
  to: "/FlatRoofsPage"
}, {
  id: "photo-3",
  file: "metal-roof-sheeting-southampton.jpg",
  caption: "Metal roof sheeting",
  alt: "Metal roof sheeting fitted on a Southampton property",
  group: "Roofs",
  to: "/RoofingServicesPage"
}, {
  id: "photo-4",
  file: "roof-restoration-dormers-southampton.jpg",
  caption: "Roof restoration with dormers",
  alt: "Roof restoration around dormer windows in Southampton",
  group: "Roofs",
  to: "/RoofingServicesPage"
}, {
  id: "photo-5",
  file: "fascia-soffit-gutter-install-southampton.jpg",
  caption: "Fascia, soffit and gutter installation",
  alt: "Fascia, soffit and gutter installation in Southampton",
  group: "Fascias & Guttering",
  to: "/FasciasSoffitsPage"
}, {
  id: "photo-6",
  file: "new-fascias-guttering-house-southampton.jpg",
  caption: "New fascias and guttering",
  alt: "New fascias and guttering on a house in Southampton",
  group: "Fascias & Guttering",
  to: "/FasciasSoffitsPage"
}, {
  id: "photo-7",
  file: "fascias-soffits-guttering-completed-southampton.jpg",
  caption: "Completed fascias, soffits and guttering",
  alt: "Completed fascias, soffits and guttering in Southampton",
  group: "Fascias & Guttering",
  to: "/FasciasSoffitsPage"
}, {
  id: "photo-8",
  file: "roofer-replacing-guttering-southampton.jpg",
  caption: "Replacing guttering",
  alt: "Roofer replacing guttering in Southampton",
  group: "Fascias & Guttering",
  to: "/GutteringPage"
}, {
  id: "photo-9",
  file: "property-exterior-renovation-southampton.jpg",
  caption: "Property roofline renovation",
  alt: "Property roofline renovation in Southampton",
  group: "Roofs",
  to: "/RoofingServicesPage"
}, {
  id: "photo-10",
  file: "upvc-guttering-fascia-southampton.jpg",
  caption: "uPVC guttering and fascia",
  alt: "uPVC guttering and fascia fitted in Southampton",
  group: "Fascias & Guttering",
  to: "/GutteringPage"
}, {
  id: "photo-11",
  file: "re-roof-strip-out-southampton.jpg",
  caption: "Re-roof strip out",
  alt: "Old roof stripped out ready for a re-roof in Southampton",
  group: "Roofs",
  to: "/NewRoofsPage"
}, {
  id: "photo-12",
  file: "epdm-flat-roof-southampton.jpg",
  caption: "EPDM flat roof",
  alt: "EPDM rubber flat roof installed in Southampton",
  group: "Flat Roofs",
  to: "/FlatRoofsPage"
}, {
  id: "photo-13",
  file: "new-tiled-roof-southampton.jpg",
  caption: "New tiled roof",
  alt: "New tiled roof completed in Southampton",
  group: "Roofs",
  to: "/NewRoofsPage"
}];
const categories = ["All", "Roofs", "Flat Roofs", "Fascias & Guttering"] as const;
type FilterType = (typeof categories)[number];
export default function Projectgallery() {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("All");
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const filteredProjects = selectedFilter === "All" ? PROJECT_PHOTOS : PROJECT_PHOTOS.filter(project => project.group === selectedFilter);
  return <section data-section-id="257196" id="projectgallery" className="bg-background text-foreground py-16 md:py-28">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        {/* Heading Block */}
        <div ref={ref} className={`text-center max-w-2xl mx-auto mb-10 md:mb-12 transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            OUR WORK
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Recent Roofing Projects in Southampton
          </h2>
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
            Explore our portfolio of roofing, flat roofing and fascia and guttering installations across Southampton, Hampshire and surrounding areas, showcasing the craftsmanship Matt and the team deliver.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center justify-center gap-2.5 mb-10 md:mb-12">
          {categories.map(cat => {
          const isActive = selectedFilter === cat;
          return <button key={cat} type="button" onClick={() => setSelectedFilter(cat)} className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "bg-card text-secondary-foreground border border-border hover:border-primary hover:text-primary"}`}>
                {cat}
              </button>;
        })}
        </div>

        {/* Responsive Grid of Project Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredProjects.map((project, i) => <Link key={project.id} to={project.to} data-index={i} className="group relative aspect-[4/3] rounded-[14px] overflow-hidden bg-muted transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 block">
              {/* Photo */}
              <img src={`${BASE_URL}${project.file}`} alt={project.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05] group-focus-visible:scale-[1.05]" />

              {/* Bottom charcoal gradient overlay (~bottom 50%) */}
              <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-[#15191D]/90 via-[#15191D]/40 to-transparent pointer-events-none" />

              {/* Category tag at top-right */}
              <div className="absolute top-3.5 right-3.5 z-10 pointer-events-none">
                <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-[#15191D]/80 backdrop-blur-sm text-white border border-white/10 font-default">
                  {project.group}
                </span>
              </div>

              {/* Project title label - always visible on all screen sizes */}
              <div className="absolute inset-x-0 bottom-0 p-4 pointer-events-none flex items-end justify-between gap-3 z-10">
                <h3 className="font-default font-semibold text-white text-sm md:text-base leading-snug drop-shadow-sm">
                  {project.caption}
                </h3>
                <span aria-hidden="true" className="shrink-0 w-8 h-8 rounded-full bg-primary/90 flex items-center justify-center text-primary-foreground opacity-90 transition-transform duration-200 group-hover:scale-110">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>)}
        </div>

        {/* View Full Gallery Link Button */}
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="h-12 rounded-lg bg-primary px-8 font-default text-base font-bold text-primary-foreground shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-hover hover:shadow-lg">
            <Link to="/GalleryPage">
              View full gallery
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}